import { Component, OnInit, Inject, ViewChild, OnDestroy } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import { Profile, Company } from 'app/models';
import { ChangeBranchesService } from 'app/services';
import { takeUntil, debounceTime } from 'rxjs/operators';
import { FormControl } from '@angular/forms';
import { Subject } from 'rxjs';
import { SelectionModel } from '@angular/cdk/collections';
import * as tableHelpers from 'app/helpers/tableSelectionHelpers';
import { environment } from 'environments/environment';



interface AddData {
  branches: Company[];
  title: string;
  user: Profile;
}

@Component({
  standalone: false,
  selector: 'app-add-branch-dialog',
  templateUrl: './add-branch-dialog.component.html',
  styleUrls: ['./add-branch-dialog.component.scss']
})
export class AddBranchDialogComponent implements OnInit, OnDestroy {
  private paginator: MatPaginator;
  displayedColumns = ['select', 'name', 'sectorNumber', 'location'];
  displayedColumnsMobile = ['name', 'select'];
  selection = new SelectionModel<Company>(true, []);
  dataSource = new MatTableDataSource([]);
  branchesCount: number;
  user: Profile;
  private sort: MatSort;
  searchResults: Company[] = [];
  searchResultsError: string;
  public branchSearchField = new FormControl();
  private onDestroy$ = new Subject<void>();
  currentPageBranches: Company[];


  @ViewChild(MatPaginator, { static: false }) set matPaginator(mp: MatPaginator) {
    this.paginator = mp;
    this.setDataSourceAttributes();
  }
  @ViewChild(MatSort, { static: false }) set matSort(ms: MatSort) {
    this.sort = ms;
    this.setDataSourceAttributes();
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: AddData,
    private _changeBranchesService: ChangeBranchesService,
    public dialogRef: MatDialogRef<AddBranchDialogComponent>
  ) { }

  ngOnInit() {
    this.data.branches = this.data.branches.filter(branch => this.isEqual(branch.status, 'Active'));
    this.dataSource.data = this.data.branches;
    this.branchesCount = this.data.branches?.length;
    this.dataSource
      .connect()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((branches) => {
        this.currentPageBranches = branches;
      });
    this.user = this.data.user;
    this.setDataSourceAttributes();
    this.subscribeToFormInput();
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
  }

  setDataSourceAttributes() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
    this.dataSource.filterPredicate = (data: Company, filter: string) =>
    data.name.toLowerCase().indexOf(filter.toLowerCase()) !== -1 || data?.sectorNumber.toLowerCase().indexOf(filter.toLowerCase()) !== -1;
  }

  monitorSearch(event) {
    this.branchSearchField.setValue(event);
  }

  subscribeToFormInput(): void {
    const searchInput$ = this.branchSearchField.valueChanges.pipe(debounceTime(200));

    searchInput$.pipe(
      takeUntil(this.onDestroy$)
    ).subscribe(data =>{
      const input = data?.trim();
      if (input.length >= environment.minimumSearchLength || input.length === 0) {
        this.handleSearch(input);
      }
    });
  }

  removeBranch(branch: Company): void {
    const branchIndex: number = this.user.branchAssignments.findIndex(userBranch => userBranch === branch.accountId);
    if (branchIndex !== -1) {
      this.user.branchAssignments.splice(branchIndex, 1);
      this._changeBranchesService.removeBranch(branch);
    }
  }

  addBranch(branch: Company): void {
    this.user.branchAssignments.push(branch.accountId);
    this._changeBranchesService.addBranch(branch);
  }

  handleSearch(data): void {
    this.dataSource.filter = data;
  }

  isAllSelected = () => tableHelpers.isAllSelected(this.currentPageBranches, this.selection.selected);

  /** Selects all rows if they are not all selected; otherwise clear selection. */
  masterToggle = () => tableHelpers.masterToggle(this.currentPageBranches, this.selection);

  isPartiallySelected = () => tableHelpers.isPartiallySelected(this.currentPageBranches, this.selection.selected);


  close() {
    this.selection.clear();
    this.dialogRef.close();
  }

  selectedBranches() {
    this.selection.selected.forEach((branch) => {
      this.user.branchAssignments.push(branch.accountId); this._changeBranchesService.addBranch(branch)
    })
  }

  isEqual(value1 : string, value2: string) : boolean{
    if(!value1 || !value2) return false;
    return value1.toLocaleLowerCase() === value2.toLocaleLowerCase();
  }
}
